import React, { useContext, useEffect, useState } from "react";
import AppSettings from "../../../../Settings/AppSettings";
import ServiceWrapper from "../../../../Services/ServiceWrapper";
import AuthManager from "../../../../Common/AuthManager";
import { AttributeContext } from "../AttributeContext";
import EditAttributeConstraint from "./EditAttributeConstraint";
import { generateAttributeConstraintObject } from "../constants";
import Accordion from "Components/DesignSystem/Accordion/Accordion";
import Alert from "Components/DesignSystem/Alert/Alert";
import Spinner from "Components/DesignSystem/Spinner/Spinner";
import Skeleton from "Components/DesignSystem/Skeleton/Skeleton";

/**
 * Edit-mode "Attribute Constraints" accordion. Fetch logic (constraintModel fast
 * path vs API, the duplicated [collapse] effects included) is verbatim; only the
 * rendering moved onto DS Accordion + Alert.
 */
const AttributeConstraint = ({
  finalAttributeConstraint,
  setFinalAttributeConstraint,
  constraintModel,
  saveMessage = null,
}) => {
  let api_base_url = AppSettings.GMS_BACKEND_API_URL;
  const attributeContextDate = useContext(AttributeContext);
  const [loading, setLoading] = useState({
    status: "",
    message: "",
  });
  const [collapse, setCollapse] = useState(false);
  const [enableEdit, setEnableEdit] = useState(false);

  const [attributeConstraint, setAttributeConstraint] = useState(null);

  useEffect(() => {
    setLoading({
      status: "",
      message: "",
    });
  }, [attributeContextDate.editingItem.id]);

  useEffect(() => {
    if (collapse && attributeConstraint === null) {
      fetchAttributes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapse]);

  useEffect(() => {
    if (collapse) {
      fetchAttributes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapse]);

  useEffect(() => {
    if (attributeConstraint) {
      setFinalAttributeConstraint(attributeConstraint);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributeConstraint]);

  async function fetchAttributes() {
    setLoading({
      status: "Loading",
      message: "Loading Attribute Constraints",
    });
    if (constraintModel) {
      setLoading({ status: "Success", message: undefined });
      const constraints = generateAttributeConstraintObject(constraintModel)
      setAttributeConstraint(constraints);
      setFinalAttributeConstraint(constraints);
    } else {
      let res = await ServiceWrapper.doGet(
        `${api_base_url}attribute/${attributeContextDate.editingItem.id}/constraints/get`,
        null,
        null,
        AuthManager.getGmsToken()
      );

      if (!res.errorfound) {
        setLoading({ status: "Success", message: undefined });
        const constraints = generateAttributeConstraintObject(res.data.data)
        setAttributeConstraint(constraints);
        setFinalAttributeConstraint(constraints);
      } else {
        setLoading({ status: "Error", message: "Error while loading details" });
      }
    }
  }

  function handle_collapse() {
    setCollapse(!collapse);
  }

  return (
    <Accordion
      title={`${enableEdit ? "Update " : ""} Attribute Constraints`}
      open={collapse}
      onOpenChange={() => handle_collapse()}
      data-testid="componets-attributeconstraint-div-1"
      variant="boxed"
    >
      <div className="tw-pb-4">
        <LoadingSuccessErrorAlert loading={loading} />

        {loading.status === "Success" && (
          <div>
            <EditAttributeConstraint
              attributeConstraint={attributeConstraint}
              setAttributeConstraint={setAttributeConstraint}
            />
          </div>
        )}
      </div>
    </Accordion>
  );
};

export default AttributeConstraint;

/* Strip-shaped status for Saving/Error/Success (§ 99 grammar: non-urgent strips
   are polite `role="status"`, Error is `role="alert"`). Loading renders skeleton
   form rows instead of a text strip — the module-wide loading grammar
   (user-requested 2026-09-01). */
function LoadingSuccessErrorAlert({ loading }) {
  if (loading.status === "Loading") {
    return (
      <div className="tw-space-y-3" data-testid="attribute-constraint-skeleton">
        <Skeleton variant="rect" width="25%" height="28px" />
        <Skeleton variant="rect" width="100%" height="28px" />
        <Skeleton variant="rect" width="100%" height="28px" />
        <Skeleton variant="rect" width="60%" height="28px" />
      </div>
    );
  }
  if (loading.status === "Saving") {
    return (
      <Alert tone="info" live data-testid="attribute-constraint-status">
        <span className="tw-inline-flex tw-items-center tw-gap-2">
          <Spinner size="sm" aria-hidden="true" />
          <span>{loading.message}</span>
        </span>
      </Alert>
    );
  }
  if (loading.status === "Error") {
    return (
      <Alert tone="danger" live data-testid="attribute-constraint-status">
        {loading.message}
      </Alert>
    );
  }
  if (loading.status === "Success" && loading.message !== undefined) {
    return (
      <Alert tone="success" live data-testid="attribute-constraint-status">
        {loading.message}
      </Alert>
    );
  }
  return null;
}
